import React, { useState, useEffect } from 'react';
import tw from 'tailwind-styled-components';
import {FaEdit, FaSave, FaTimes, FaPlus, FaEllipsisV, FaTrash, FaMapMarkerAlt } from 'react-icons/fa';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { StatesList } from '../../utils/constants';
import { MdCheckCircleOutline } from 'react-icons/md';
import useAddress from '../../hooks/useAddress';
import MuiSelect from '../Styles/MuiSelect';

const addressFormat = /^[a-zA-Z0-9\s.,'/-]{10,300}$/;

const AddressSection = ({ openOnAddAddress }) => {
  const {addresses, fetchUserAddresses, deleteAddress, updateAddress, createNewAddress, setAsDefaultAddress, loading, selectedAddressId, setSelectedAddressId, error: addressError
  } = useAddress();

  const [addressDeleteError, setAddressDeleteError] = useState('');
  const [showAddNewAddress, setShowAddNewAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [errors, setErrors] = useState({});
  const [newAddressForm, setNewAddressForm] = useState({
    address_line_1: '', address_line_2: '', city: '', state: '', postal_code: '',country: 'India', type: 'home', is_default: true
  });
  const [dropdownOpen, setDropdownOpen] = useState(null);

  useEffect(() => {
    fetchUserAddresses();
  }, [fetchUserAddresses]);

  const validateAddress = (name, value) => {
    if (value === undefined || value === null) value = '';
    value = String(value);

    switch (name) {
      case 'address_line_1':
        if (!value.trim()) return 'Address line 1 is required';
        if (value.trim().length < 5) return 'Address line 1 must be at least 5 characters';
        if (value.trim().length > 100) return 'Address line 1 must be less than 100 characters';
        return '';

      case 'address_line_2':
        if (value.trim() && value.trim().length > 100) return 'Address line 2 must be less than 100 characters';
        return '';

      case 'city':
        if (!value.trim()) return 'City is required';
        if (value.trim().length < 2) return 'City must be at least 2 characters';
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) return 'City can only contain letters and spaces';
        return '';

      case 'state':
        if (!value.trim()) return 'State is required';
        if (value.trim().length < 2) return 'State must be at least 2 characters';
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) return 'State can only contain letters and spaces';
        return '';

      case 'postal_code':
        if (!value.trim()) return 'Postal code is required';
        if (!/^[0-9]{6}$/.test(value.trim())) return 'Please provide a valid 6-digit postal code';
        return '';

      case 'country':
        if (value.trim() && !/^[a-zA-Z\s]+$/.test(value.trim())) return 'Country can only contain letters and spaces';
        return '';

      case 'address':
        if (value.trim() && !addressFormat.test(value.trim())) {
          return 'Address must be 10-300 characters and contain only letters, numbers, spaces, and basic punctuation (.,\'/-)';
        }
        return '';
      default:
        return '';
    }
  };

  const handleInputChange = (field, value) => {
    setNewAddressForm({ ...newAddressForm, [field]: value });
    const error = validateAddress(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleSave = () => {
    // Validate all fields before submitting
    const newErrors = {};
    ['address_line_1', 'city', 'state', 'postal_code'].forEach(field => {
      const error = validateAddress(field, newAddressForm[field]);
      if (error) newErrors[field] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    if (editingAddressId) {
      updateAddress(editingAddressId, newAddressForm);
      setEditingAddressId(null);
    } else {
      createNewAddress(newAddressForm);
    }

    // Reset Form
    setShowAddNewAddress(false);
    setErrors({});
    setNewAddressForm({
      address_line_1: '', address_line_2: '', city: '', state: '', postal_code: '',country: 'India', type: 'home', is_default: true
    });
  };

  const handleDeleteClick = (address) => {
    if (editingAddressId === address.id) {
      setAddressDeleteError("Please close the edit form for this address before deleting it.");
      setTimeout(() => setAddressDeleteError(''), 4000); // Clear message
    } else {
      deleteAddress(address.id);
    }
    setDropdownOpen(null);
  };

  useEffect(() => {
    if (openOnAddAddress) {
      setShowAddNewAddress(true);
    }
  }, [openOnAddAddress]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen !== null) {
        setDropdownOpen(null);
      }
    };

    if (dropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <FieldGroup>
      <SectionHeader>
        <SectionTitle>All Addresses</SectionTitle>
        <AddNewButton onClick={() => setShowAddNewAddress(!showAddNewAddress)}>
          <FaPlus className="w-4 h-4 mr-2" />
          Add New Address
        </AddNewButton>
      </SectionHeader>

      {addressDeleteError && <ErrorMessage>{addressDeleteError}</ErrorMessage>}
      {addressError && <ErrorMessage>{addressError}</ErrorMessage>}

      <AddressList>
        {addresses.length > 0 ? (
          addresses.map((address) => (
            <AddressCard key={address.id} $isSelected={selectedAddressId === address.id}>
              <AddressContent onClick={() => setSelectedAddressId(address.id)}>
                <AddressIcon>
                  <FaMapMarkerAlt className="w-4 h-4 text-gray-600" />
                </AddressIcon>
                <AddressDetailsContainer>
                  <AddressHeader>
                    <AddressLine>
                      {address.address_line_1}
                      {address.address_line_2 && `, ${address.address_line_2}`}
                    </AddressLine>
                    <AddressTypeBadge type={address.type || 'home'}>
                      {address.type === 'work' ? 'Work' : address.type === 'other' ? 'Other' : 'Home'}
                    </AddressTypeBadge>
                  </AddressHeader>
                  <AddressDetails>
                    {address.city}, {address.state} - {address.postal_code}
                  </AddressDetails>
                  {address.country && <AddressDetails>{address.country}</AddressDetails>}
                  {address.is_default && <DefaultBadge>Default</DefaultBadge>}
                </AddressDetailsContainer>
              </AddressContent>
              <AddressActions>
                <DropdownButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(dropdownOpen === address.id ? null : address.id);
                  }}
                >
                  <FaEllipsisV className="w-4 h-4" />
                </DropdownButton>
                {dropdownOpen === address.id && (
                  <DropdownMenu>
                    <DropdownItem onClick={() => {
                      setEditingAddressId(address.id);
                      setNewAddressForm(address);
                      setShowAddNewAddress(true);
                      setDropdownOpen(null);
                    }}>
                      <FaEdit className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownItem>
                    {!address.is_default && (
                      <DropdownItem onClick={() => {
                        setAsDefaultAddress(address.id);
                        setDropdownOpen(null);
                      }}>
                        <MdCheckCircleOutline className="w-4 h-4 mr-2" />
                        Set as Default
                      </DropdownItem>
                    )}
                    <DropdownItem
                      danger
                      onClick={() => handleDeleteClick(address)}
                    >
                      <FaTrash className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </AddressActions>
            </AddressCard>
          ))
        ) : (
          <NoAddressMessage>
            No address found<br />Click "Add New Address" to add your first address.
          </NoAddressMessage>
        )}
      </AddressList>

      {/* Add New Address Form */}
      {showAddNewAddress && (
        <AddressForm>
          <FormSectionTitle>
            {editingAddressId ? 'Edit Address' : 'Add New Address'}
          </FormSectionTitle>

          <FormFieldRow>
            <FormField>
              <FormLabel>Address Type <span className="text-red-500">*</span></FormLabel>
              <MuiSelect
                value={newAddressForm.type || 'home'}
                onChange={(e) => setNewAddressForm({ ...newAddressForm, type: e.target.value })}
                options={[
                  { value: 'home', label: 'Home' },
                  { value: 'work', label: 'Work' },
                  { value: 'other', label: 'Other' },
                ]}
              />
            </FormField>

            <FormField>
              <DefaultCheckboxContainer>
                <DefaultCheckbox
                  type="checkbox"
                  checked={newAddressForm.is_default}
                  onChange={(e) => setNewAddressForm({ ...newAddressForm, is_default: e.target.checked })}
                />
                <DefaultCheckboxLabel>Set as Default</DefaultCheckboxLabel>
              </DefaultCheckboxContainer>
            </FormField>
          </FormFieldRow>

          <FormField>
            <FormLabel>Address Line 1 <span className="text-red-500">*</span></FormLabel>
            <FormInput
              type="text"
              value={newAddressForm.address_line_1}
              placeholder="Enter your address"
              onChange={(e) => handleInputChange('address_line_1', e.target.value)}
            />
            {errors.address_line_1 && <ErrorText>{errors.address_line_1}</ErrorText>}
          </FormField>

          <FormField>
            <FormLabel>Address Line 2</FormLabel>
            <FormInput
              type="text"
              value={newAddressForm.address_line_2}
              placeholder="Enter your address (optional)"
              onChange={(e) => setNewAddressForm({ ...newAddressForm, address_line_2: e.target.value })}
            />
          </FormField>

          <FormFieldRow>
            <FormField>
              <FormLabel>City <span className="text-red-500">*</span></FormLabel>
              <FormInput
                type="text"
                value={newAddressForm.city}
                placeholder="Enter city"
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
              {errors.city && <ErrorText>{errors.city}</ErrorText>}
            </FormField>

            <FormField>
              <FormLabel>State <span className="text-red-500">*</span></FormLabel>
              <Autocomplete
                disablePortal
                id="combo-box-states"
                options={StatesList}
                value={newAddressForm.state || null}
                onChange={(event, newValue) => {
                  handleInputChange('state', newValue || '');
                }}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    padding: '3px',
                    borderRadius: '0.5rem',
                    borderColor: '#e5e7eb',
                  }
                }}
                renderInput={(params) => <TextField {...params} placeholder="Select State" />}
              />
              {errors.state && <ErrorText>{errors.state}</ErrorText>}
            </FormField>
          </FormFieldRow>

          <FormFieldRow>
            <FormField>
              <FormLabel>Postal Code <span className="text-red-500">*</span></FormLabel>
              <FormInput
                type="text"
                value={newAddressForm.postal_code}
                placeholder="Enter postal code"
                onChange={(e) => handleInputChange('postal_code', e.target.value)}
              />
              {errors.postal_code && <ErrorText>{errors.postal_code}</ErrorText>}
            </FormField>

            <FormField>
              <FormLabel>Country</FormLabel>
              <FormInput
                type="text"
                value={newAddressForm.country}
                placeholder="Enter country"
                onChange={(e) => handleInputChange('country', e.target.value)}
              />
              {errors.country && <ErrorText>{errors.country}</ErrorText>}
            </FormField>
          </FormFieldRow>

          <AddressFormButtons>
            <SaveButton
              onClick={handleSave}
              disabled={loading}
            >
              <FaSave className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : editingAddressId ? 'Update' : 'Save Address'}
            </SaveButton>
            <CancelButton
              onClick={() => {
                setShowAddNewAddress(false);
                setEditingAddressId(null);
                setNewAddressForm({ address_line_1: '', address_line_2: '', city: '', state: '', postal_code: '', country: 'India', type: 'home', is_default: true });
              }}
            >
              <FaTimes className="w-4 h-4 mr-2" />
              Cancel
            </CancelButton>
          </AddressFormButtons>
        </AddressForm>
      )}
    </FieldGroup>
  );
};

// Styled Components
const FieldGroup = tw.div`
  space-y-4 p-4 md:p-6 border border-gray-200 rounded-lg bg-white
`;

const SectionHeader = tw.div`
  flex items-center justify-between mb-4
`;

const SectionTitle = tw.h3`
  text-base md:text-lg font-medium text-gray-900
`;

const AddNewButton = tw.button`
  bg-kuality-blue hover:bg-kuality-dark text-white px-3 py-2 rounded-lg font-medium transition-colors flex items-center text-sm
`;

const AddressList = tw.div`
  space-y-3 mb-4
`;

const AddressCard = tw.div`
  ${props => props.$isSelected ? 'border-slate-500 bg-slate-50' : 'border-gray-200 bg-white hover:bg-gray-50'}
  border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 flex items-center justify-between
`;

const AddressContent = tw.div`
  flex items-start flex-1 min-w-0
`;

const AddressIcon = tw.div`
  flex-shrink-0 mr-3 mt-1
`;

const AddressDetailsContainer = tw.div`
  flex-1 min-w-0
`;

const AddressHeader = tw.div`
  flex items-center justify-between mb-1
`;

const AddressLine = tw.p`
  text-gray-900 font-medium text-sm truncate
`;

const AddressTypeBadge = tw.span`
  ${props =>
    props.type === 'work' ? 'bg-blue-100 text-blue-800' :
      props.type === 'other' ? 'bg-gray-100 text-gray-800' :
        'bg-orange-100 text-orange-800'
  }
  text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ml-2
`;

const AddressDetails = tw.p`
  text-gray-600 text-sm mt-1
`;

const DefaultBadge = tw.span`
  inline-block bg-kuality-bg text-kuality-blue text-xs px-2 py-1 rounded-full mt-2 font-medium
`;

const AddressActions = tw.div`
  relative flex-shrink-0 ml-2
`;

const DropdownButton = tw.button`
  p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors
`;

const DropdownMenu = tw.div`
  absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1
`;

const DropdownItem = tw.button`
  ${props => props.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}
  w-full px-3 py-2 text-left text-sm flex items-center transition-colors
`;

const AddressForm = tw.div`
  mt-4 p-4 bg-white border border-gray-200 rounded-lg space-y-4
`;

const FormSectionTitle = tw.h4`
  text-base font-medium text-gray-900 mb-4
`;

const FormFieldRow = tw.div`
  grid grid-cols-1 md:grid-cols-2 gap-4
`;

const FormField = tw.div`
  space-y-2
`;

const FormLabel = tw.label`
  block text-sm font-medium text-gray-700
`;

const DefaultCheckboxContainer = tw.div`
  flex items-center mt-10 ml-28
`;

const DefaultCheckbox = tw.input`
  w-4 h-4 text-slate-600 bg-white border-gray-300 rounded focus:ring-slate-500 focus:ring-2
`;

const DefaultCheckboxLabel = tw.label`
  ml-2 text-sm font-medium text-gray-700 cursor-pointer
`;

const FormInput = tw.input`
  w-full px-3 py-2 md:py-3 border border-gray-300 rounded-lg focus:outline-none text-gray-900 placeholder-gray-400
`;

const AddressFormButtons = tw.div`
  flex justify-end space-x-3 pt-4 border-t border-gray-200
`;

const SaveButton = tw.button`
  bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px]
`;

const CancelButton = tw.button`
  bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-1 ml-6 rounded-lg font-medium transition-colors flex items-center justify-center
`;

const NoAddressMessage = tw.div`
  text-center py-8 px-4 text-gray-500 bg-white text-md 
`;

const ErrorMessage = tw.div`
  text-red-700 py-3 px-4 bg-red-50 border border-red-200 rounded-lg mb-4 font-medium text-center
 `;
const ErrorText = tw.div`
   text-red-600 text-sm font-medium
 `;

export default AddressSection;
